import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export function exitDocFullscreenIfNeeded(): void {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void>;
  };
  if (!doc.fullscreenElement && !doc.webkitFullscreenElement) return;
  const exit = document.exitFullscreen || doc.webkitExitFullscreen;
  if (exit) {
    try {
      void exit.call(document);
    } catch {
      /* ignore */
    }
  }
}

/**
 * 관제 팝업 전용 html/body 스타일. 반드시 cleanup으로 되돌려
 * (안 그러면 「돌아가기」 후 pool-management 등이 검정·스크롤 잠금으로 깨짐)
 */
export function applyMonitoringPopupDocumentChrome(background = '#000'): () => void {
  const htmlEl = document.documentElement;
  const body = document.body;
  const prev = {
    htmlMargin: htmlEl.style.margin,
    htmlHeight: htmlEl.style.height,
    bodyMargin: body.style.margin,
    bodyHeight: body.style.height,
    bodyOverflow: body.style.overflow,
    bodyBackground: body.style.background,
  };
  htmlEl.style.margin = '0';
  htmlEl.style.height = '100%';
  body.style.margin = '0';
  body.style.height = '100%';
  body.style.overflow = 'hidden';
  body.style.background = background;

  return () => {
    htmlEl.style.margin = prev.htmlMargin;
    htmlEl.style.height = prev.htmlHeight;
    body.style.margin = prev.bodyMargin;
    body.style.height = prev.bodyHeight;
    body.style.overflow = prev.bodyOverflow;
    body.style.background = prev.bodyBackground;
    exitDocFullscreenIfNeeded();
  };
}

/** iframe(srcDoc) 모니터링 HTML에서 보낸 닫기 요청 처리 */
export function useCctvPopupCloseMessage(): void {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inplace = searchParams.get('inplace') === '1';
  const returnToRaw = searchParams.get('returnTo');

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string } | null;
      if (data?.type !== 'cctv-monitoring-close') return;

      exitDocFullscreenIfNeeded();

      const returnTo =
        returnToRaw && returnToRaw.startsWith('/') ? returnToRaw : null;

      // 동일 창 전체화면/순환: history에서 팝업 항목을 제거해야
      // 「돌아가기」 후 브라우저 뒤로가기가 다시 전체화면으로 가지 않음.
      // (returnTo 로 push 하면 [관리 → 전체화면 → 관리]가 되어 뒤로가기가 전체화면)
      if (inplace) {
        const histIdx = (window.history.state as { idx?: number } | null)?.idx;
        if (typeof histIdx === 'number' && histIdx > 0) {
          navigate(-1);
          return;
        }
        if (returnTo) {
          navigate(returnTo, { replace: true });
          return;
        }
        navigate(-1);
        return;
      }

      if (returnTo) {
        navigate(returnTo, { replace: true });
        return;
      }

      try {
        window.close();
      } catch {
        /* ignore */
      }
      window.setTimeout(() => {
        if (!window.closed) {
          try {
            navigate(-1);
          } catch {
            /* ignore */
          }
        }
      }, 120);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [inplace, returnToRaw, navigate]);
}
